'use client';

import type { BoxProps } from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';

import { forwardRef } from 'react';

import Box from '@mui/material/Box';
import NoSsr from '@mui/material/NoSsr';

import { RouterLink } from 'src/routes/components';

import { CONFIG } from 'src/config-global';
import { formatAppVersionLabel } from 'src/constants/app-environment';

import { logoClasses } from './classes';

// ----------------------------------------------------------------------

export type LogoProps = BoxProps & {
  href?: string;
  disableLink?: boolean;
  showVersion?: boolean;
};

export const Logo = forwardRef<HTMLDivElement, LogoProps>(
  (
    {
      width = 40,
      height = 40,
      disableLink = false,
      showVersion = true,
      className,
      href = '/',
      sx,
      ...other
    },
    ref
  ) => {
    /*
     * OR using local (public folder)
     * const logo = ( <Box alt="logo" component="img" src={`${CONFIG.site.basePath}/logo/logo-single.svg`} width={width} height={height} /> );
     */

    const logo = (
      <Box
        alt="logo"
        component="img"
        src={`${CONFIG.site.basePath}/logo/logo-single.svg`}
        width={width}
        height={height}
      />
    );

    const rootClassName = logoClasses.root.concat(className ? ` ${className}` : '');

    const baseRootSx: SxProps<Theme> = {
      flexShrink: 0,
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0.25,
      verticalAlign: 'middle',
      maxWidth: '100%',
    };

    const rootSx: SxProps<Theme> = sx
      ? [baseRootSx, ...(Array.isArray(sx) ? sx : [sx])]
      : baseRootSx;

    return (
      <NoSsr
        fallback={
          <Box
            width={width}
            height={height}
            className={rootClassName}
            sx={rootSx}
          />
        }
      >
        <Box
          ref={ref}
          className={rootClassName}
          sx={rootSx}
          {...other}
        >
          <Box
            component={RouterLink}
            href={href}
            width={width}
            height={height}
            aria-label="logo"
            sx={{
              flexShrink: 0,
              display: 'inline-flex',
              ...(disableLink && { pointerEvents: 'none' }),
            }}
          >
            {logo}
          </Box>

          {showVersion && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                maxWidth: '100%',
                fontSize: '0.625rem',
                lineHeight: 1.2,
                fontWeight: 500,
                color: 'text.secondary',
                opacity: 0.64,
                userSelect: 'none',
                textAlign: 'center',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {formatAppVersionLabel(
                CONFIG.site.version,
                CONFIG.site.appEnvironment,
                CONFIG.site.buildRevision
              )}
            </Box>
          )}
        </Box>
      </NoSsr>
    );
  }
);
